import React, { 
  useCallback, 
  useEffect, 
  useState 
} from 'react';
import { 
  Alert, 
  Button, 
  Form, 
  Input, 
  Modal, 
  Popconfirm, 
  Space, 
  Switch, 
  Table, 
  Tag, 
  Typography, 
  message 
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import LevelChip from '../../layout/context/LevelChip';
import PersonPicker from '../people/PersonPicker';
import { useCapability } from '../../hooks/useSelfCapabilities';
import {
  belongsTo,
  createGroup,
  deleteGroup,
  Group,
  groupErrorMessage,
  listGroups,
  updateGroup
} from '../../../service/groups.service';

type Level = 'organisation' | 'entity' | 'workspace';

interface Props {
  nodeId: number;
  level: Level;
}

const GroupsTab: React.FC<Props> = ({ nodeId, level }) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [inherited, setInherited] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<Group | 'new' | null>(null);
  const [saving, setSaving] = useState(false);
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [form] = Form.useForm();
  // Showing "New group" needs create rights on this node; the server enforces it anyway.
  const canCreate = useCapability('MEMBERSHIP', 'manage', nodeId);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return listGroups(nodeId)
      .then((out) => {
        setGroups(out.groups);
        setInherited(out.inherited);
      })
      .catch((e) => setError(groupErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [nodeId]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (target: Group | 'new') => {
    setEditing(target);
    if (target === 'new') {
      form.resetFields();
      setMemberIds([]);
    } else {
      form.setFieldsValue({
        name: target.name,
        description: target.description,
        archived: target.archived
      });
      setMemberIds(target.members.map((m) => m.id));
    }
  };

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    const input = {
      name: values.name.trim(),
      description: (values.description || '').trim(),
      member_ids: memberIds,
      archived: Boolean(values.archived)
    };
    try {
      if (editing === 'new') await createGroup(nodeId, input);
      else if (editing) await updateGroup(nodeId, editing.id, input);
      message.success('Group saved');
      setEditing(null);
      await load();
    } catch (e) {
      message.error(groupErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (group: Group) => {
    try {
      await deleteGroup(nodeId, group.id);
      message.success('Group deleted');
      await load();
    } catch (e) {
      message.error(groupErrorMessage(e));
    }
  };

  const hasBelow = groups.some((g) => g.home_node && g.home_node.id !== nodeId);

  const columns: any[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (name: string, g: Group) => (
        <span>
          {name} {g.archived && <Tag>Archived</Tag>}
        </span>
      )
    }, { 
      title: 'Description', 
      dataIndex: 'description', 
      render: (d: string) => d || '-' 
    }, { 
      title: 'Members', 
      render: (_: unknown, g: Group) => g.members.length 
    }, {
      title: 'Belongs to',
      render: (_: unknown, g: Group) => {
        const b = belongsTo(g, nodeId, level);
        return b.own ? (
          b.text
        ) : (
          <Space size={4}>
            {b.level && <LevelChip level={b.level} />}
            {b.text}
          </Space>
        );
      }
    }
  ];
  const visibleColumns = hasBelow || inherited.length ? columns : columns.filter((c) => c.title !== 'Belongs to');
  const ownColumns = [
    ...visibleColumns, {
      title: '',
      align: 'right',
      render: (_: unknown, g: Group) =>
        g.editable ? (
          <Space>
            <Button size="small" onClick={() => open(g)}>Edit</Button>
            <Popconfirm
              title="Delete this group?"
              description="Groups with assignment history cannot be deleted; archive them instead."
              okText="Delete"
              onConfirm={() => remove(g)}
            >
              <Button size="small" danger>Delete</Button>
            </Popconfirm>
          </Space>
        ) : null
    }
  ];

  const editingGroup = editing && editing !== 'new' ? editing : null;
  const pickerNode = editingGroup?.home_node?.id ?? nodeId;

  return (
    <div className="flex flex-col gap-4">
      {error && <Alert type="error" showIcon message={error} />}
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-gray-500">
          Groups share responsibility for assets. Membership never grants a role.
        </div>
        {canCreate && (
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => open('new')}
          >New group</Button>
        )}
      </div>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={ownColumns}
        dataSource={groups}
        pagination={false}
        locale={{ emptyText: 'No groups yet' }}
      />
      {inherited.length > 0 && (
        <div>
          <div className="font-medium text-black">Inherited</div>
          <div 
            className="text-sm text-gray-500 mb-2"
          >Managed at a higher level. Shown here read-only.</div>
          <Table
            rowKey="id"
            size="small"
            className="opacity-70"
            columns={visibleColumns}
            dataSource={inherited}
            pagination={false}
          />
        </div>
      )}
      <Modal
        open={editing !== null}
        title={editing === 'new' ? 'New group' : 'Edit group'}
        okText="Save"
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setEditing(null)}
        destroyOnClose
      >
        <Form 
          form={form} 
          layout="vertical" 
          initialValues={{ archived: false }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ 
              required: true, 
              whitespace: true, 
              message: 'Enter a name' 
            }]}
          >
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
          <Form.Item label="Members">
            <PersonPicker
              mode="multiple"
              scope={{ 
                kind: 'node', 
                nodeId: pickerNode, 
                purpose: 'group_member' 
              }}
              value={memberIds}
              onChange={(v) => setMemberIds(v.filter((x): x is string => typeof x === 'string'))}
              placeholder="Add people"
            />
            {editingGroup && editingGroup.members.length > 0 && (
              <Typography.Text type="secondary" className="text-xs">
                Current: {editingGroup.members.map((m) => m.email).join(', ')}
              </Typography.Text>
            )}
          </Form.Item>
          {editingGroup && (
            <Form.Item 
              name="archived" 
              label="Archived" 
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default GroupsTab;
